import createRaf from "@solid-primitives/raf";
import { batch, createSignal, For, onCleanup, onMount, Show } from "solid-js";
import { game } from "~/js/game";
import { getItem, giveItem, giveItems, ItemCard, itemQualities, itemRarity, takeItems } from "~/js/items";
import { createNotification } from "~/js/notifications";
import { addStat } from "~/js/stats";
import { createPage, isContainerItem, isMasterItem, isStickerItem, randomStream } from "~/js/utils";
import { displayWinnings } from "~/js/winnings";
import { global, showPage } from "~/script";
import type { ContainerItem, ItemName, Quality, SkinItem, StickerItem } from "~/types";
import { user } from "~/userStore";

let itemsToBeRevealed: ItemName[] = [];

export function openCaseNavigateAway() {
	console.log({ itemsToBeRevealed });

	for (const itemName of itemsToBeRevealed) {
		const item = getItem(itemName);
		createNotification({
			message: q("span", [
				q("span", "You opened a "),
				q("span", {
					text: item.name,
					style: { color: itemRarity[item.rarity].color },
				}),
				q("span", "!").parentNode,
			]),
		});
		global.noSpoilers = false;
	}
	itemsToBeRevealed = [];
}

function instantOpenContainer(container: ContainerItem, numberToOpen: number) {
	const baseItems = container.content.items.reduce(
		(acc, itemName) => {
			const item = getItem(itemName);
			if ("stattrak" in item && item.stattrak) return acc;

			acc[item.rarity] ??= [];
			acc[item.rarity]!.push(itemName);
			return acc;
		},
		{} as Record<keyof typeof itemRarity, ItemName[]>,
	);

	const containersTaken = takeItems(container, numberToOpen);
	if (containersTaken < numberToOpen) {
		throw new Error(`Tried to open ${numberToOpen} containers, but only ${containersTaken} were taken from inventory!`);
	}
	if (container.keyName) {
		const keysTaken = takeItems(getItem(container.keyName), numberToOpen);
		if (keysTaken < numberToOpen) {
			throw new Error(`Tried to open ${numberToOpen} containers, but only ${keysTaken} keys were taken from inventory!`);
		}
	}
	let openedCases = 0;
	let openedCapsules = 0;
	let totalXp = 0;
	let totalOpenedR0 = 0;
	let totalOpenedR1 = 0;
	let totalOpenedR2 = 0;
	let totalOpenedR3 = 0;
	let totalOpenedR4 = 0;
	let totalOpenedR5 = 0;
	let totalOpenedR6 = 0;
	let totalOpenedR7 = 0;

	const unboxedItems = Array.from({ length: numberToOpen }, () => {
		if (container.type === "case") openedCases++;
		if (container.type === "capsule") openedCapsules++;

		const item = getItemFromContainer(baseItems);
		const itemXp = (item.price / Math.max(Math.log(item.price / 10), 10) / 3) * user.upgrades.xpmul * user.upgrades.xpMulMul; // TODO: have one spot for xp calculations
		totalXp += itemXp;
		switch (item.rarity) {
			case 0:
				totalOpenedR0++;
				break;
			case 1:
				totalOpenedR1++;
				break;
			case 2:
				totalOpenedR2++;
				break;
			case 3:
				totalOpenedR3++;
				break;
			case 4:
				totalOpenedR4++;
				break;
			case 5:
				totalOpenedR5++;
				break;
			case 6:
				totalOpenedR6++;
				break;
			case 7:
				totalOpenedR7++;
				break;
		}
		return item;
	});
	batch(() => {
		user.xp += totalXp;
		addStat("earned_xp", totalXp);
		addStat("opened_cases", openedCases);
		addStat("opened_capsules", openedCapsules);
		addStat("opened_rarities_0", totalOpenedR0);
		addStat("opened_rarities_1", totalOpenedR1);
		addStat("opened_rarities_2", totalOpenedR2);
		addStat("opened_rarities_3", totalOpenedR3);
		addStat("opened_rarities_4", totalOpenedR4);
		addStat("opened_rarities_5", totalOpenedR5);
		addStat("opened_rarities_6", totalOpenedR6);
		addStat("opened_rarities_7", totalOpenedR7);
	});
	giveItems(unboxedItems);
	displayWinnings({
		title: `Items from ${numberToOpen} containers!`,
		returnBtn: { text: "Go to inventory", page: "inventory" },
		items: unboxedItems,
		sortBy: "value",
	});
	game.emit("update", true);
}

function PageWrapper(props: { containerName: ItemName; amountToOpen: number }) {
	return (
		<Show fallback={<div>Aht aht aht</div>} when={props.containerName.length > 0 && props.amountToOpen > 0}>
			<ContainerPage amountToOpen={props.amountToOpen} containerName={props.containerName} />
		</Show>
	);
}

function ContainerPage(props: { containerName: ItemName; amountToOpen: number }) {
	const container = getItem(props.containerName) as ContainerItem;
	const baseItems = container.content.items.reduce(
		(acc, itemName) => {
			const item = getItem(itemName);
			if ("stattrak" in item && item.stattrak) return acc;

			acc[item.rarity] ??= [];
			acc[item.rarity]!.push(itemName);
			return acc;
		},
		{} as Record<keyof typeof itemRarity, ItemName[]>,
	);
	onCleanup(() => {
		openCaseNavigateAway();
		global.noSpoilers = false;
	});
	return (
		<div class="opencase-wheels">
			<For each={Array.from({ length: props.amountToOpen })}>{() => <ContainerWheel baseItems={baseItems} container={container} />}</For>
		</div>
	);
}

function ContainerWheel(props: { container: ContainerItem; baseItems: Record<keyof typeof itemRarity, ItemName[]> }) {
	const numElements = props.container.content.items.length;

	const [running, start, stop] = createRaf(calcScroll);
	const [scrollPos, setScrollPos] = createSignal(0);

	onMount(() => {
		if (props.container.type === "case") addStat("opened_cases");
		if (props.container.type === "capsule") addStat("opened_capsules");

		start();
	});
	global.noSpoilers = true;

	const wonItemIdx = Math.round(Math.random() * numElements);
	const items = Array.from({ length: numElements }, (_, index) => ({
		item: getItemFromContainer(props.baseItems),
		isWinner: index === wonItemIdx,
	}));

	const item = items[wonItemIdx]!.item;

	giveItem(item.name);
	const itemXp = item.price / Math.max(Math.log(item.price / 10), 10) / 3;
	// global vars
	itemsToBeRevealed.push(item.name);
	const tempXp = itemXp * user.upgrades.xpmul * user.upgrades.xpMulMul;

	addStat(`opened_rarities_${item.rarity}`);

	let startTime: number | null = null;
	const randomScrollOffset = ((Math.random() - 0.5) / numElements) * 0.6;
	function easeOutQuint(x: number): number {
		return 1 - (1 - x) ** 3;
	}
	function calcScroll(t: DOMHighResTimeStamp) {
		startTime ??= t;
		const elapsed = t - startTime;
		const duration = 5000;
		const numberOfSpins = 10;
		if (elapsed >= duration) {
			setScrollPos(wonItemIdx / numElements + randomScrollOffset);
			stop();
			onFinish();
			return;
		}
		setScrollPos(
			(easeOutQuint(elapsed / duration) * numberOfSpins +
				(wonItemIdx / numElements + randomScrollOffset) * easeOutQuint(elapsed / duration)) %
				1,
		);
	}

	let hasfinished = false;
	function onFinish() {
		if (hasfinished) return;
		hasfinished = true;
		user.xp += tempXp;
		addStat("earned_xp", tempXp);
		global.noSpoilers = false;
		itemsToBeRevealed.splice(itemsToBeRevealed.indexOf(item.name), 1);
		game.emit("update", true);
	}

	return (
		<div class="opencase-wheel" classList={{ "opening-finished": !running() }}>
			<For each={items}>
				{(item, index) => (
					<div
						style={{
							position: "absolute",
							left: "50%",
							transform: `translateX(${(index() - scrollPos() * numElements - numElements * Math.floor((index() - scrollPos() * numElements) / numElements + 0.5)) * 100 - 50}%)`,
						}}
					>
						{item.isWinner ? <ItemCard class="case-item-won" clickable={true} item={item.item} /> : <ItemCard item={item.item} />}
					</div>
				)}
			</For>
		</div>
	);
}

// Globals to pass data into the page, prolly a better way to do this but eh
let containerName = "" as ItemName;
let amountToOpen = 0;
createPage(() => <PageWrapper amountToOpen={amountToOpen} containerName={containerName} />, "opencase");

// https://csgoskins.gg/blog/csgo-case-odds-the-official-numbers-published-by-valve
function getRarity<T extends keyof typeof itemRarity>(availableRarities: T[]): T {
	// Closed-form geometric thresholds with small first/second tier offsets to boost fallthrough.
	// Base geometric: p_i = 0.8 * 0.2^i (for i < n-1). Tail = leftover.
	// We emulate CS:GO observed tail (~0.26%) by slightly reducing first two thresholds.
	const r = Math.random();

	for (let i = 0; i < availableRarities.length - 1; i++) {
		// Geometric partial sum S_i = base * (1 - decay^{i+1}) / (1 - decay)
		let threshold = (0.8 * (1 - 0.2 ** (i + 1))) / (1 - 0.2);
		if (i >= 0) threshold -= 0.0008; // apply first offset to all thresholds >= first
		if (i >= 1) threshold -= 0.0002; // apply second offset starting from second
		if (r < threshold) {
			return availableRarities[i]!;
		}
	}
	return availableRarities[availableRarities.length - 1]!;
}
// https://blog.csfloat.com/analysis-of-float-value-and-paint-seed-distribution-in-cs-go/
// https://blog.csfloat.com/how-csgo-float-values-work/
function getFloat(min = 0, max = 1): number {
	// Define global buckets
	const buckets = [
		{ min: 0.0, max: 0.07, weight: 0.03 },
		{ min: 0.08, max: 0.15, weight: 0.24 },
		{ min: 0.16, max: 0.38, weight: 0.33 },
		{ min: 0.39, max: 0.45, weight: 0.24 },
		{ min: 0.46, max: 1.0, weight: 0.16 },
	] as const;

	const f = randomStream.randomFloat(0, 1);
	let cumulative = 0;
	let selectedBucket: (typeof buckets)[number] = buckets[4]; // default to last bucket

	for (const bucket of buckets) {
		cumulative += bucket.weight;
		if (f <= cumulative) {
			selectedBucket = bucket;
			break;
		}
	}
	const midFloat =
		selectedBucket.min + ((f - (cumulative - selectedBucket.weight)) / selectedBucket.weight) * (selectedBucket.max - selectedBucket.min);
	return midFloat * (max - min) + min;
}

// round float to nearest quality available until we do item migration
export function getWear(availableQualities: Quality[]): Quality {
	const buckets = [
		{ min: 0.46, max: 1.0, weight: 0.16 },
		{ min: 0.39, max: 0.45, weight: 0.24 },
		{ min: 0.16, max: 0.38, weight: 0.33 },
		{ min: 0.08, max: 0.15, weight: 0.24 },
		{ min: 0.0, max: 0.07, weight: 0.03 },
	] as const;
	const minQual = Math.max(...availableQualities);
	const maxQual = Math.min(...availableQualities);
	const minFloat = buckets[minQual - 1]!.min;
	const maxFloat = buckets[maxQual - 1]!.max;
	const wear = getFloat(minFloat, maxFloat);
	if (wear < 0.07) return 5;
	if (wear < 0.15) return 4;
	if (wear < 0.38) return 3;
	if (wear < 0.45) return 2;
	return 1;
}

export function openContainer(containerNameArg: ItemName, numberToOpenArg: number, isInstantOpen: boolean) {
	const container = getItem(containerNameArg);
	if (!isContainerItem(container)) return;
	if (!isInstantOpen) {
		global.noSpoilers = true;
	} else {
		instantOpenContainer(container, numberToOpenArg);
		return;
	}

	const containersTaken = takeItems(container, numberToOpenArg);
	if (containersTaken < numberToOpenArg) {
		throw new Error(`Tried to open ${numberToOpenArg} containers, but only ${containersTaken} were taken from inventory!`);
	}
	if (container.keyName) {
		const keysTaken = takeItems(getItem(container.keyName), numberToOpenArg);
		if (keysTaken < numberToOpenArg) {
			throw new Error(`Tried to open ${numberToOpenArg} containers, but only ${keysTaken} keys were taken from inventory!`);
		}
	}
	containerName = containerNameArg;
	amountToOpen = numberToOpenArg;
	showPage("opencase", false, false, true);
}

function getItemFromContainer(baseItems: Record<keyof typeof itemRarity, ItemName[]>): SkinItem | StickerItem {
	const rarity = getRarity(Object.keys(baseItems).map((e) => parseInt(e, 10)) as (keyof typeof itemRarity)[]);
	const targetItem = getItem(baseItems[rarity][Math.floor(Math.random() * baseItems[rarity].length)]!);
	if (isStickerItem(targetItem)) {
		return targetItem;
	}

	if (isMasterItem(targetItem)) {
		const makeStattrak = Math.random() < 0.1 && targetItem.stattrakAvailable;
		const makeSouvenir = Math.random() < 0.02 && targetItem.souvenirAvailable;

		if (targetItem.skin === "Vanilla")
			return getItem(`${(makeStattrak ? "StatTrak " : "") + targetItem.short} (Vanilla)` as ItemName) as SkinItem;
		const quality = getWear(targetItem.qualities!);

		const finalItemName = `${(makeStattrak ? "StatTrak " : "") + (makeSouvenir ? "Souvenir " : "") + targetItem.short} (${itemQualities[quality]})`;

		return getItem(finalItemName as ItemName) as SkinItem;
	}
	throw Error("There should be no non-sticker/non-skin items in containers");
}
